/** 태블릿 인지 훈련 QR — buildCognitiveResultShareUrl (v1, type=cognitive) */

export type CognitiveScorePair = {
  correct: number | null;
  total: number | null;
};

export type CognitiveSessionResultQueryV1 = {
  v: 1;
  type: 'cognitive';
  sid: string;
  visualSearch: CognitiveScorePair;
  memory: CognitiveScorePair;
  reasoning: CognitiveScorePair;
  /** 세션 소요 시간(초). 없으면 null */
  sec: number | null;
  t: number | null;
  src: string;
  set: number | null;
};

function parseOptionalNumber(value: string | null): number | null {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function decodeParam(value: string | null): string {
  if (value == null) return '';
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

export function isCognitiveSessionQuery(search: string | URLSearchParams): boolean {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
      : search;
  return params.get('type')?.trim().toLowerCase() === 'cognitive';
}

/** URLSearchParams → 인지 세션 결과 (필수 필드 없으면 null) */
export function parseCognitiveSessionResultQuery(
  search: string | URLSearchParams,
): CognitiveSessionResultQueryV1 | null {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
      : search;

  if (params.get('v') !== '1') return null;
  if (!isCognitiveSessionQuery(params)) return null;

  const sid = decodeParam(params.get('sid')).trim();
  if (!sid) return null;

  const visualSearch = {
    correct: parseOptionalNumber(params.get('vs')),
    total: parseOptionalNumber(params.get('vsT')),
  };
  const memory = {
    correct: parseOptionalNumber(params.get('mem')),
    total: parseOptionalNumber(params.get('memT')),
  };
  const reasoning = {
    correct: parseOptionalNumber(params.get('rsn')),
    total: parseOptionalNumber(params.get('rsnT')),
  };

  const hasAnyScore =
    visualSearch.correct != null ||
    memory.correct != null ||
    reasoning.correct != null;

  if (!hasAnyScore) return null;

  const secRaw = parseOptionalNumber(params.get('sec'));
  const sec = secRaw == null || secRaw < 0 ? null : Math.round(secRaw);

  return {
    v: 1,
    type: 'cognitive',
    sid,
    visualSearch,
    memory,
    reasoning,
    sec,
    t: parseOptionalNumber(params.get('t')),
    src: decodeParam(params.get('src')).trim() || 'tablet',
    set: parseOptionalNumber(params.get('set')),
  };
}

export function displayScorePair(
  pair: CognitiveScorePair,
  fallback = '--',
): { correct: string; total: string } {
  return {
    correct: pair.correct == null ? fallback : String(Math.round(pair.correct)),
    total: pair.total == null ? fallback : String(Math.round(pair.total)),
  };
}

/** sec=83 → "1분 23초" */
export function formatSessionDurationSec(sec: number | null): string {
  if (sec == null) return '';
  const total = Math.max(0, Math.round(sec));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}분 ${String(seconds).padStart(2, '0')}초`;
}
